import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { GlobalService } from './global.service';
import { RealtimeService } from './realtime.service';

/**
 * Interface que representa a estrutura de um registo de feedback ou anomalia.
 */
export interface AdminFeedbackClass {
  idAdminFeedback?: number;
  UUID?: string;
  FeedbackType: 'Bug' | 'Melhoria' | 'Sugestão';
  Title: string;
  Description: string;
  Priority: 'Baixa' | 'Normal' | 'Alta' | 'Crítica';
  Status?:
    | 'Pendente'
    | 'Em Análise'
    | 'Em Desenvolvimento'
    | 'Fazer Mais Tarde'
    | 'Pedir Informação'
    | 'Aguarda Deploy'
    | 'Em Produção'
    | 'Validado & Concluído'
    | 'Rejeitado'
    | 'Anulado';
  RejectionReason?: string;
  Observation?: string;
  CommentsJson?: string;
  Area?: string;
  RoutePath?: string;
  ComponentName?: string;
  BrowserInfo?: string;
  ScreenshotPath?: string;
  AttachmentPath?: string;
  ImagesJson?: string;
  AIPrompt?: string;
  AppliedDate?: string;
  AppliedBy?: string;
  DateStart?: string;
  DateUpdate?: string;
  DateResolved?: string;
  ReportedByName?: string;
  ReportedByEmail?: string;
  CreatedByName?: string;
  CreatedByEmail?: string;
  AssignedToName?: string;
  Device?: 'Desktop' | 'Telemóvel' | 'Ambos';
  MediatorScope?: boolean;
  draggingType?: 'screenshot' | 'attachment' | null;
  isUploadingAttachment?: 'screenshot' | 'attachment' | null;
  isUploadingExtraImages?: boolean;
}

export interface AdminFeedbackTeamPreference {
  idAdminFeedbackTeamPreference?: number;
  Name: string;
  Email?: string;
  NotifyByEmail: boolean | number;
}

/**
 * Definição estrutural de uma Área Funcional do sistema para fins estatísticos e de triagem.
 */
export interface FeedbackAreaDefinition {
  key: string;
  label: string;
  icon: string;
  color: string;
  badgeBg: string;
}

export const FEEDBACK_AREAS: FeedbackAreaDefinition[] = [
  { key: 'Área de Cliente', label: 'Área de Cliente', icon: '👤', color: '#0284c7', badgeBg: '#e0f2fe' },
  { key: 'Área do Parceiro', label: 'Área do Parceiro', icon: '🤝', color: '#0d9488', badgeBg: '#ccfbf1' },
  { key: 'Teleconsulta', label: 'Teleconsulta', icon: '🩺', color: '#059669', badgeBg: '#d1fae5' },
  { key: 'CRM Mediador', label: 'CRM Mediador', icon: '🛡️', color: '#2563eb', badgeBg: '#dbeafe' },
  { key: 'CRM ADM', label: 'CRM ADM', icon: '💼', color: '#7c3aed', badgeBg: '#ede9fe' },
  { key: 'Configurações & Gestão', label: 'Configurações & Gestão', icon: '⚙️', color: '#ea580c', badgeBg: '#ffedd5' },
  { key: 'Homepage & Simulações', label: 'Homepage & Simulações', icon: '🌐', color: '#16a34a', badgeBg: '#dcfce7' },
  { key: 'Módulos Globais', label: 'Módulos Globais', icon: '🧩', color: '#64748b', badgeBg: '#f1f5f9' },
];

export const MEDIATOR_FEEDBACK_AREAS: FeedbackAreaDefinition[] = [
  { key: 'CRM Mediador', label: 'CRM Mediador', icon: '🛡️', color: '#2563eb', badgeBg: '#dbeafe' },
  { key: 'Teleconsulta', label: 'Teleconsulta', icon: '🩺', color: '#059669', badgeBg: '#d1fae5' },
  { key: 'Área de Cliente', label: 'Área de Cliente', icon: '👤', color: '#0284c7', badgeBg: '#e0f2fe' },
];

/**
 * Destinatário padrão de todos os novos tickets (e fallback para tickets existentes sem destinatário explícito).
 */
export const DEFAULT_FEEDBACK_ASSIGNEE = 'João Vilar';

/**
 * Lista de administradores disponíveis para autoria e atribuição dos pedidos de feedback.
 */
export const FEEDBACK_AUTHORS: string[] = [
  'João Vilar',
  'Hugo Capela',
  'André Venda',
  'João Fernandes',
  'Rute Da Silva Pequeno',
];

export const FEEDBACK_ASSIGNEES: string[] = [
  'João Vilar',
  'Hugo Capela',
  'André Venda',
  'João Fernandes',
  'Rute Da Silva Pequeno',
];

/**
 * Interface que agrega os contadores estatísticos dos tickets de feedback por estado.
 */
export interface AdminFeedbackSummary {
  Total: number;
  Pendente: number;
  EmAnalise: number;
  EmDesenvolvimento: number;
  FazerMaisTarde: number;
  AguardaDeploy?: number;
  EmProducao: number;
  ValidadoConcluido?: number;
  Rejeitado: number;
  Anulado: number;
  PedirInformacao: number;
  ResolvedToday?: number;
  ResolvedYesterday?: number;
  ResolvedLast7Days?: number;
  ResolvedThisMonth?: number;
  ResolvedThisYear?: number;
  AvgResolutionMinutes?: number;
}

export interface FeedbackCommentEntry {
  id?: number;
  text: string;
  author: string;
  authorUuid?: string;
  authorEmail?: string;
  date: string;
  editedAt?: string;
  type?: 'comment' | 'reopen' | 'resolution' | 'assignment' | 'status';
  previousStatus?: string;
  newStatus?: string;
  previousAssignee?: string;
  newAssignee?: string;
}

export interface TicketInterventionInfo {
  timestamp: number;
  type: 'reopen' | 'comment' | 'assignment' | 'status' | 'new';
  author: string;
  text: string;
  date: string;
  commentCount: number;
  userCommentCount: number;
  isReopened: boolean;
  reopenCount: number;
  latestUserComment?: FeedbackCommentEntry | null;
  latestReopenComment?: FeedbackCommentEntry | null;
}

/**
 * Serviço responsável pela comunicação com a API de feedback do painel de administração.
 * Permite registar, listar, atualizar estados e enviar comentários de tickets.
 *
 * @module AdminFeedback
 */
@Injectable({
  providedIn: 'root',
})
export class AdminFeedbackService {
  private baseUrl = environment.URL + '/feedback';
  private sessionConsoleLogs: { level: string; message: string; timestamp: string }[] = [];
  private isConsoleHooked = false;
  private isRealtimeNotificationsHooked = false;
  private activeViewerCount = 0;
  private recentNotificationKeys = new Map<string, number>();
  private readonly ACTIVE_IDENTITY_STORAGE_KEY = 'sv_admin_feedback_active_identity_v1';

  /** Emite o ID do ticket quando o utilizador clica em "Ver ticket" numa notificação discreta */
  public readonly openTicketRequest$ = new Subject<number>();

  constructor(
    private http: HttpClient,
    private globalService: GlobalService,
    private realtimeService: RealtimeService,
    private injector: Injector,
  ) {
    this.initGlobalConsoleBuffer();
    this.initRealtimeNotifications();
  }

  private initGlobalConsoleBuffer(): void {
    if (this.isConsoleHooked || typeof window === 'undefined') return;
    this.isConsoleHooked = true;

    const originalLog = console.log.bind(console);
    const originalWarn = console.warn.bind(console);
    const originalError = console.error.bind(console);

    const formatArg = (a: any): string => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) {
        const stackLine = a.stack ? a.stack.split('\n')[1]?.trim() || '' : '';
        return stackLine ? `${a.name}: ${a.message} (${stackLine})` : `${a.name}: ${a.message}`;
      }
      if (
        a &&
        typeof a === 'object' &&
        (a.name === 'HttpErrorResponse' || (typeof a.status === 'number' && ('url' in a || 'statusText' in a)))
      ) {
        const detail =
          typeof a.error === 'string' ? a.error : a.error?.message || a.error?.error || a.message || a.statusText || '';
        return `HttpErrorResponse [${a.status}] ${a.url || ''}: ${detail}`.trim();
      }
      try {
        return JSON.stringify(a, null, 0);
      } catch {
        return String(a);
      }
    };

    const noisyPatterns = [
      /\[UserAvatar\]/i,
      /\[Showcase\]/i,
      /\[DialogManager\]\s+Diálogo\s+.*(aberto|fechado)/i,
      /Socket\.IO Monitoring (conectado|desconectado)/i,
      /\[IdleService\]\s+(Atividade detectada|Sessão terminada)/i,
      /\[SessionService\]\s+Renovação automática/i,
      /\[AuthService\]\s+🔄?\s*Token (renovado|com \d+ min)/i,
      /\[ApiKeyInterceptor\]/i,
      /\[LoadDefaultData\]\s+(A obter dados|Resposta do|Estado Actived|Perfil destino|A gerar novo token|Token obtido|profileData tem PhotoPath)/i,
      /restoreSession:\s*Nenhuma sessão ativa/i,
      /Angular is running in development mode/i,
      /Navigation triggered outside Angular zone/i,
      /using deprecated parameters/i,
      /WebAssembly\.instantiateStreaming/i,
    ];

    const isRelevantForTicket = (level: string, args: any[], message: string): boolean => {
      const msg = (message || '').trim();
      if (!msg) return false;
      if (noisyPatterns.some(pattern => pattern.test(msg))) return false;

      if (level === 'error' || level === 'window.error' || level === 'unhandledrejection') {
        return true;
      }
      if (level === 'warn') {
        return true;
      }

      // Para console.log normal, guardar apenas se contiver um objeto de erro real (ex.: error => console.log(error))
      // ou indicar explicitamente uma falha/erro/exceção relevante para diagnóstico
      const hasErrorArg = args.some(
        a =>
          a instanceof Error ||
          (a &&
            typeof a === 'object' &&
            (a.name === 'HttpErrorResponse' ||
              a.error instanceof Error ||
              (typeof a.status === 'number' && a.status >= 400))),
      );
      if (hasErrorArg) return true;

      return (
        /\b(error|erro|exce[cç][aã]o|exception|falha|failed|http\s*failure|unauthorized|forbidden)\b/i.test(msg) ||
        msg.includes('❌')
      );
    };

    const pushLogEntry = (level: string, rawMessage: string): void => {
      const truncated = rawMessage.substring(0, 800);
      const last = this.sessionConsoleLogs[this.sessionConsoleLogs.length - 1];
      if (last && last.level === level && last.message === truncated) {
        last.timestamp = new Date().toISOString();
        return;
      }
      this.sessionConsoleLogs.push({
        level,
        message: truncated,
        timestamp: new Date().toISOString(),
      });
      if (this.sessionConsoleLogs.length > 100) {
        this.sessionConsoleLogs = this.sessionConsoleLogs.slice(-100);
      }
    };

    const capture = (level: string, original: (...args: any[]) => void) => {
      return (...args: any[]) => {
        original(...args);
        try {
          const message = args.map(formatArg).join(' ');
          if (!isRelevantForTicket(level, args, message)) return;
          pushLogEntry(level, message);
        } catch {
          // silenciar
        }
      };
    };

    console.log = capture('log', originalLog);
    console.warn = capture('warn', originalWarn);
    console.error = capture('error', originalError);

    window.addEventListener('error', (event: ErrorEvent) => {
      try {
        const message = `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`;
        if (isRelevantForTicket('window.error', [event.error], message)) {
          pushLogEntry('window.error', message);
        }
      } catch {
        // silenciar
      }
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;
        const msg = reason ? formatArg(reason) : 'Unhandled Promise Rejection';
        if (isRelevantForTicket('unhandledrejection', [reason], msg)) {
          pushLogEntry('unhandledrejection', msg);
        }
      } catch {
        // silenciar
      }
    });
  }

  getConsoleLogs(): { level: string; message: string; timestamp: string }[] {
    return [...this.sessionConsoleLogs];
  }

  /**
   * Listar todos os tickets (com filtros opcionais)
   */
  getAll(filters?: {
    status?: string;
    type?: string;
    priority?: string;
    resolvedDate?: string;
    mediatorScope?: boolean;
    reporterUuid?: string;
    reporterEmail?: string;
    reporterName?: string;
  }): Observable<any> {
    let params: any = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.type) params.type = filters.type;
    if (filters?.priority) params.priority = filters.priority;
    if (filters?.resolvedDate) params.resolvedDate = filters.resolvedDate;
    if (filters?.mediatorScope) {
      params.mediatorScope = '1';
      if (filters.reporterUuid) params.reporterUuid = filters.reporterUuid;
      if (filters.reporterEmail) params.reporterEmail = filters.reporterEmail;
      if (filters.reporterName) params.reporterName = filters.reporterName;
    }

    return this.http.get<any>(`${this.baseUrl}/list`, { params });
  }

  /**
   * Obter um ticket pelo ID
   */
  getById(id: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/${id}`);
  }

  /**
   * Obter contadores resumo por estado
   */
  getSummary(filters?: {
    mediatorScope?: boolean;
    reporterUuid?: string;
    reporterEmail?: string;
    reporterName?: string;
  }): Observable<any> {
    const params: any = {};
    if (filters?.mediatorScope) {
      params.mediatorScope = '1';
      if (filters.reporterUuid) params.reporterUuid = filters.reporterUuid;
      if (filters.reporterEmail) params.reporterEmail = filters.reporterEmail;
      if (filters.reporterName) params.reporterName = filters.reporterName;
    }
    return this.http.get<any>(`${this.baseUrl}/summary`, { params });
  }

  /**
   * Detetar automaticamente a Área Funcional a partir da rota e/ou tag do componente.
   * Dá prioridade à rota (URL) porque componentes partilhados (ex: IntermediaryareaComponent,
   * CrmCustomersComponent, CrmPoliciesComponent) são reutilizados em múltiplas áreas
   * (/intermediaryarea, /commercialarea, /crm-mediador, /crm).
   */
  detectAreaFromContext(routePath?: string, componentName?: string): string {
    let route = (routePath || '').toLowerCase();
    try {
      if (route.includes('://')) {
        const url = new URL(route);
        route = url.pathname.toLowerCase();
      }
    } catch (_) {
      const idx = route.indexOf('://');
      if (idx !== -1) {
        const slashIdx = route.indexOf('/', idx + 3);
        if (slashIdx !== -1) {
          route = route.substring(slashIdx);
        }
      }
    }
    const comp = (componentName || '').toLowerCase();

    // 1. Prioridade à Rota Angular (específica -> geral)
    if (
      route.includes('/teleconsulta') ||
      route.includes('/telemedicina') ||
      route.startsWith('teleconsulta') ||
      route.startsWith('telemedicina')
    ) {
      return 'Teleconsulta';
    }

    if (
      route.includes('/crm-mediador') ||
      route.startsWith('crm-mediador') ||
      route.includes('/commercialarea') ||
      route.startsWith('commercialarea')
    ) {
      return 'CRM Mediador';
    }

    if (route.includes('/crm') || route.startsWith('crm')) {
      return 'CRM ADM';
    }

    if (
      route.includes('/intermediaryarea') ||
      route.startsWith('intermediaryarea') ||
      route.includes('/project-manager')
    ) {
      return 'Área do Parceiro';
    }

    if (
      route.includes('/clientarea') ||
      route.startsWith('clientarea') ||
      route.includes('consultar-sinistro') ||
      route.includes('participar-sinistro')
    ) {
      return 'Área de Cliente';
    }

    if (
      route.includes('sitemanagement') ||
      route.includes('/admin') ||
      route.includes('vault') ||
      route.includes('permissions') ||
      route.includes('sendinvitations')
    ) {
      return 'Configurações & Gestão';
    }

    if (route === '/' || route === '/home' || route.includes('seguros') || route.includes('guias')) {
      return 'Homepage & Simulações';
    }

    // 2. Fallback por nome/tag do componente quando a rota não é conclusiva
    if (comp.includes('teleconsulta') || comp.includes('telemedicina')) {
      return 'Teleconsulta';
    }

    if (comp.includes('crm-mediador') || comp.includes('commercialarea')) {
      return 'CRM Mediador';
    }

    if (comp.includes('intermediaryarea') || comp.includes('project-manager')) {
      return 'Área do Parceiro';
    }

    if (comp.includes('clientarea') || comp.includes('insuranceclaims')) {
      return 'Área de Cliente';
    }

    if (
      comp.includes('/crm/') ||
      comp.includes('pages/crm') ||
      comp.includes('contacts') ||
      comp.includes('pipeline') ||
      comp.includes('automations') ||
      comp.includes('email-marketing')
    ) {
      return 'CRM ADM';
    }

    if (
      comp.includes('sitemanagement') ||
      comp.includes('accessmanager') ||
      comp.includes('userpermissions') ||
      comp.includes('sendinvitations')
    ) {
      return 'Configurações & Gestão';
    }

    if (comp.includes('pages/home') || comp.includes('seguro-detail') || comp.includes('blog')) {
      return 'Homepage & Simulações';
    }

    return 'Módulos Globais';
  }

  /**
   * Resolve a Área Funcional efetiva de um ticket existente, normalizando aliases
   * e rotas (ex: 'Área do Mediador' -> 'CRM Mediador', 'CRM' -> 'CRM ADM', 'Telemedicina' -> 'Teleconsulta').
   */
  resolveTicketArea(ticket: { Area?: string; RoutePath?: string; ComponentName?: string } | null | undefined): string {
    if (!ticket) return 'Módulos Globais';
    const rawArea = (ticket.Area || '').trim();
    const route = (ticket.RoutePath || '').toLowerCase();

    if (route.includes('/teleconsulta') || route.includes('/telemedicina')) {
      return 'Teleconsulta';
    }

    if (route.includes('/crm-mediador') || route.includes('/commercialarea')) {
      if (
        !rawArea ||
        rawArea === 'CRM' ||
        rawArea === 'CRM ADM' ||
        rawArea === 'Área do Parceiro' ||
        rawArea === 'Área do Mediador'
      ) {
        return 'CRM Mediador';
      }
    }

    if ((route.includes('/crm/') || route.endsWith('/crm')) && !route.includes('/crm-mediador')) {
      if (!rawArea || rawArea === 'Área do Parceiro' || rawArea === 'Área do Mediador' || rawArea === 'CRM') {
        return 'CRM ADM';
      }
    }

    if (rawArea) {
      if (rawArea === 'CRM' || rawArea === 'CRM ADM') return 'CRM ADM';
      if (rawArea === 'Área do Mediador') return 'CRM Mediador';
      if (rawArea === 'Telemedicina') return 'Teleconsulta';
      return rawArea;
    }

    return this.detectAreaFromContext(ticket.RoutePath, ticket.ComponentName);
  }

  /**
   * Obtém a definição visual (ícone, cor, label) de uma Área Funcional.
   */
  getAreaDefinition(areaKey: string): FeedbackAreaDefinition {
    const key = (areaKey || '').trim();
    let normalizedKey = key;
    if (key === 'CRM') {
      normalizedKey = 'CRM ADM';
    } else if (key === 'Área do Mediador') {
      normalizedKey = 'CRM Mediador';
    } else if (key === 'Telemedicina') {
      normalizedKey = 'Teleconsulta';
    }
    return (
      FEEDBACK_AREAS.find(
        a => a.key === normalizedKey || a.label === normalizedKey || a.key === key || a.label === key,
      ) || FEEDBACK_AREAS[FEEDBACK_AREAS.length - 1]
    );
  }

  /**
   * Criar novo ticket de feedback com screenshot, anexo e imagens adicionais
   */
  create(
    feedback: AdminFeedbackClass,
    screenshot?: Blob,
    attachment?: File,
    extraImages?: (File | Blob)[],
  ): Observable<any> {
    const formData = new FormData();

    const loggedUser = this.globalService.loggedUserClass;
    const userName = loggedUser?.Name || loggedUser?.customerClass?.Name || 'Administrador';
    const userEmail = loggedUser?.Email || loggedUser?.customerClass?.Email || '';
    const detectedArea = feedback.Area || this.detectAreaFromContext(feedback.RoutePath, feedback.ComponentName);

    formData.append('UUID', loggedUser?.UUID || '');
    formData.append('ReportedByName', feedback.ReportedByName || userName);
    formData.append('ReportedByEmail', feedback.ReportedByEmail || userEmail);
    formData.append('AssignedToName', feedback.AssignedToName || DEFAULT_FEEDBACK_ASSIGNEE);
    formData.append('Device', feedback.Device || 'Desktop');
    formData.append('FeedbackType', feedback.FeedbackType);
    formData.append('Title', feedback.Title);
    formData.append('Description', feedback.Description);
    formData.append('Priority', feedback.Priority);
    formData.append('Area', detectedArea);
    formData.append('RoutePath', feedback.RoutePath || '');
    formData.append('ComponentName', feedback.ComponentName || '');
    formData.append('BrowserInfo', feedback.BrowserInfo || '');
    if (feedback.MediatorScope) {
      formData.append('MediatorScope', '1');
    }

    if (screenshot) {
      formData.append('screenshot', screenshot, 'screenshot.png');
    }
    if (attachment) {
      formData.append('attachment', attachment);
    }
    if (extraImages && extraImages.length > 0) {
      extraImages.forEach((img, index) => {
        if (img instanceof File) {
          formData.append('images', img, img.name);
        } else {
          formData.append('images', img, `imagem_${index + 1}.png`);
        }
      });
    }

    return this.http.post<any>(`${this.baseUrl}/create`, formData);
  }

  /**
   * Actualizar estado de um ticket
   */
  updateStatus(
    id: number,
    status: string,
    rejectionReason?: string,
    options?: { authorName?: string; reopenReason?: string },
  ): Observable<any> {
    const loggedUser = this.globalService.loggedUserClass;
    const defaultAuthor = loggedUser?.Name || loggedUser?.customerClass?.Name || 'Administrador';
    return this.http.put<any>(`${this.baseUrl}/${id}/status`, {
      Status: status,
      RejectionReason: rejectionReason || null,
      authorName: options?.authorName || defaultAuthor,
      reopenReason: options?.reopenReason || null,
      UUID: loggedUser?.UUID || '',
    });
  }

  /**
   * Reclassificar/Editar dados de um ticket (Tipo, Prioridade, Atribuição/Passagem, etc.)
   */
  update(
    id: number,
    data: Partial<AdminFeedbackClass> & {
      actorName?: string;
      authorName?: string;
      assignmentNote?: string;
      passNote?: string;
    },
  ): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${id}`, {
      ...data,
      UUID: this.globalService.loggedUserClass?.UUID || '',
    });
  }

  /**
   * Soft delete de um ticket
   */
  remove(id: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/${id}`, {
      body: { UUID: this.globalService.loggedUserClass?.UUID || '' },
    });
  }

  /**
   * Remover anexo individual (screenshot ou attachment)
   */
  removeAttachment(id: number, type: 'screenshot' | 'attachment'): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/${id}/attachment/${type}`);
  }

  /**
   * Carregar ou alterar anexo individual (screenshot ou attachment) de um ticket existente
   */
  uploadAttachment(id: number, type: 'screenshot' | 'attachment', file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.baseUrl}/${id}/attachment/${type}`, formData);
  }

  /**
   * Carregar múltiplas imagens adicionais para um ticket existente
   */
  uploadImages(id: number, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach(f => formData.append('images', f, f.name));
    return this.http.post<any>(`${this.baseUrl}/${id}/images`, formData);
  }

  /**
   * Remover uma imagem individual de um ticket existente
   */
  removeImage(id: number, imagePath: string): Observable<any> {
    return this.http.request<any>('delete', `${this.baseUrl}/${id}/image`, {
      body: { imagePath },
    });
  }

  /**
   * Guardar / actualizar observação num ticket
   */
  updateObservation(id: number, observation: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${id}/observation`, {
      observation,
      UUID: this.globalService.loggedUserClass?.UUID || '',
    });
  }

  /**
   * Adicionar comentário/resposta a um ticket (com suporte opcional a reabertura imediata)
   */
  addComment(
    id: number,
    text: string,
    options?: { authorName?: string; reopen?: boolean; newStatus?: string },
  ): Observable<any> {
    const loggedUser = this.globalService.loggedUserClass;
    const defaultAuthor = loggedUser?.Name || loggedUser?.customerClass?.Name || 'Utilizador';
    return this.http.post<any>(`${this.baseUrl}/${id}/comment`, {
      text,
      authorName: options?.authorName || defaultAuthor,
      reopen: options?.reopen || false,
      newStatus: options?.newStatus || undefined,
      UUID: loggedUser?.UUID || '',
    });
  }

  /**
   * Editar um comentário existente de um ticket (apenas pelo próprio autor)
   */
  updateComment(
    id: number,
    commentId: number | string,
    text: string,
    options?: { authorName?: string },
  ): Observable<any> {
    const loggedUser = this.globalService.loggedUserClass;
    const defaultAuthor = loggedUser?.Name || loggedUser?.customerClass?.Name || 'Utilizador';
    return this.http.put<any>(`${this.baseUrl}/${id}/comment/${encodeURIComponent(String(commentId))}`, {
      text,
      authorName: options?.authorName || defaultAuthor,
      UUID: loggedUser?.UUID || '',
    });
  }

  /**
   * Eliminar um comentário de um ticket (apenas pelo próprio autor)
   */
  deleteComment(id: number, commentId: number | string, options?: { authorName?: string }): Observable<any> {
    const loggedUser = this.globalService.loggedUserClass;
    const defaultAuthor = loggedUser?.Name || loggedUser?.customerClass?.Name || 'Utilizador';
    return this.http.request<any>('delete', `${this.baseUrl}/${id}/comment/${encodeURIComponent(String(commentId))}`, {
      body: {
        authorName: options?.authorName || defaultAuthor,
        UUID: loggedUser?.UUID || '',
      },
    });
  }

  /**
   * Verifica se o utilizador autenticado é o autor do comentário ("o próprio") e pode editá-lo ou eliminá-lo
   */
  canModifyComment(
    ticket: AdminFeedbackClass | null | undefined,
    comment: FeedbackCommentEntry | null | undefined,
    context?: { isMediatorScope?: boolean; selectedAuthor?: string },
  ): boolean {
    if (!comment || !comment.text) return false;
    if (this.isAutomatedResolutionComment(comment)) return false;
    if (this.isAssignmentComment(comment) || this.isStatusComment(comment)) return false;

    const u: any = this.globalService?.loggedUserClass;
    const myUuid = String(u?.UUID || '').trim();
    const myEmail = String(u?.commercialClass?.Email || u?.Email || u?.customerClass?.Email || '')
      .trim()
      .toLowerCase();

    const candidateNames = new Set<string>();
    const addCandidate = (val?: string | null) => {
      const v = String(val || '')
        .trim()
        .toLowerCase();
      if (v) candidateNames.add(v);
    };
    addCandidate(u?.Name);
    addCandidate(u?.customerClass?.Name);
    addCandidate(u?.commercialClass?.Name);
    addCandidate(u?.CommercialName);
    addCandidate(context?.selectedAuthor);

    const commentUuid = String(comment.authorUuid || '').trim();
    const commentEmail = String(comment.authorEmail || '')
      .trim()
      .toLowerCase();
    const commentAuthor = String(comment.author || '')
      .trim()
      .toLowerCase();

    if (myUuid && commentUuid && myUuid === commentUuid) {
      return true;
    }
    if (myEmail && commentEmail && myEmail === commentEmail) {
      return true;
    }
    if (commentAuthor && candidateNames.has(commentAuthor)) {
      return true;
    }

    if (context?.isMediatorScope) {
      const ticketReporter = String(ticket?.ReportedByName || ticket?.CreatedByName || '')
        .trim()
        .toLowerCase();
      if (
        commentAuthor === 'utilizador' ||
        (ticketReporter &&
          commentAuthor === ticketReporter &&
          commentAuthor !== 'administrador' &&
          !FEEDBACK_AUTHORS.some(a => a.toLowerCase() === commentAuthor))
      ) {
        return true;
      }
      return false;
    }

    const nif = String(u?.NIF || u?.customerClass?.NIF || '').trim();
    const rawName = String(u?.Name || u?.customerClass?.Name || '')
      .trim()
      .toLowerCase();
    const isSharedAdmin =
      nif === '111111111' ||
      nif === '516749471' ||
      rawName === 'administrador' ||
      rawName === 'admin' ||
      rawName.includes('administrador') ||
      myEmail === 'admin@segurosvirtual.pt' ||
      myEmail.includes('admin@') ||
      !rawName;

    if (isSharedAdmin) {
      return true;
    }

    if (!commentUuid) {
      const ticketReporter = String(ticket?.ReportedByName || ticket?.CreatedByName || '')
        .trim()
        .toLowerCase();
      if (
        commentAuthor === 'administrador' ||
        commentAuthor === 'utilizador' ||
        (ticketReporter && commentAuthor === ticketReporter)
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Obter ordem personalizada (Drag & Drop) do servidor
   */
  getCustomOrder(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/order`);
  }

  /**
   * Guardar ordem personalizada (Drag & Drop) no servidor e localmente
   */
  saveCustomOrder(orderIds: number[]): Observable<any> {
    const cleanIds = Array.isArray(orderIds) ? orderIds.map(id => Number(id)).filter(id => !isNaN(id) && id > 0) : [];
    this.saveLocalCustomOrder(cleanIds);
    return this.http.put<any>(`${this.baseUrl}/order`, {
      orderIds: cleanIds,
      UUID: this.globalService.loggedUserClass?.UUID || '',
    });
  }

  saveLocalCustomOrder(orderIds: number[]): void {
    try {
      const cleanIds = Array.isArray(orderIds) ? orderIds.map(id => Number(id)).filter(id => !isNaN(id) && id > 0) : [];
      localStorage.setItem('admin_feedback_custom_order', JSON.stringify(cleanIds));
    } catch {
      // ignore storage errors
    }
  }

  getLocalCustomOrder(): number[] {
    try {
      const raw = localStorage.getItem('admin_feedback_custom_order');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(id => Number(id)).filter(id => !isNaN(id) && id > 0) : [];
    } catch {
      return [];
    }
  }

  // ============================================================================
  // DETEÇÃO DE REABERTURA, COMENTÁRIOS DE UTILIZADOR E ESTADO "POR ABRIR / VISTO"
  // ============================================================================

  private readonly SEEN_STORAGE_KEY = 'sv_admin_feedback_seen_v1';
  private readonly SEEN_BASELINE_KEY = 'sv_admin_feedback_seen_baseline_v1';
  private seenCache: Record<number, number> | null = null;

  private readonly CLOSED_STATUSES = ['Em Produção', 'Validado & Concluído', 'Aguarda Deploy', 'Rejeitado', 'Anulado'];

  private readonly OPEN_STATUSES = [
    'Pendente',
    'Em Análise',
    'Em Desenvolvimento',
    'Pedir Informação',
    'Fazer Mais Tarde',
  ];

  /**
   * Faz parse seguro dos comentários de um ticket
   */
  parseTicketComments(ticket: AdminFeedbackClass | null | undefined): FeedbackCommentEntry[] {
    if (!ticket || !ticket.CommentsJson) return [];
    try {
      const parsed = JSON.parse(ticket.CommentsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Verifica se um comentário é um registo de reabertura
   */
  isReopenComment(comment: FeedbackCommentEntry | null | undefined): boolean {
    if (!comment || !comment.text) return false;
    if (comment.type === 'reopen') return true;
    const txt = comment.text.trim();
    if (txt.startsWith('🔄')) return true;
    return /ticket reaberto|reabriu o ticket|reaberto para|voltou a acontecer|problema persiste|não ficou resolvido/i.test(
      txt,
    );
  }

  /**
   * Verifica se um comentário é o guia automático de resolução (🚀)
   */
  isAutomatedResolutionComment(comment: FeedbackCommentEntry | null | undefined): boolean {
    if (!comment || !comment.text) return false;
    if (comment.type === 'reopen' || comment.type === 'assignment' || comment.type === 'status') return false;
    return comment.text.trim().startsWith('🚀');
  }

  /**
   * Verifica se um comentário é um registo de atribuição / passagem de ticket (↪️)
   */
  isAssignmentComment(comment: FeedbackCommentEntry | null | undefined): boolean {
    if (!comment || !comment.text) return false;
    if (comment.type === 'assignment') return true;
    return comment.text.trim().startsWith('↪️');
  }

  /**
   * Verifica se um comentário é um registo de transição de estado / criação (🔀 / 🆕)
   */
  isStatusComment(comment: FeedbackCommentEntry | null | undefined): boolean {
    if (!comment || !comment.text) return false;
    if (comment.type === 'status') return true;
    const txt = comment.text.trim();
    return txt.startsWith('🔀') || txt.startsWith('🆕');
  }

  /**
   * Resolve a quem está dirigido/atribuído um ticket (usa AssignedToName, ou última passagem em CommentsJson, ou default João Vilar)
   */
  resolveTicketAssignee(ticket: AdminFeedbackClass | null | undefined): string {
    if (!ticket) return DEFAULT_FEEDBACK_ASSIGNEE;
    if (ticket.AssignedToName && String(ticket.AssignedToName).trim()) {
      return String(ticket.AssignedToName).trim();
    }
    const comments = this.parseTicketComments(ticket);
    for (let i = comments.length - 1; i >= 0; i--) {
      const c = comments[i];
      if (c && c.type === 'assignment' && c.newAssignee && String(c.newAssignee).trim()) {
        return String(c.newAssignee).trim();
      }
    }
    return DEFAULT_FEEDBACK_ASSIGNEE;
  }

  /**
   * Devolve apenas os comentários/informações escritos por utilizadores (exclui guias automáticos 🚀 e logs automáticos de criação 🆕)
   */
  getUserComments(ticket: AdminFeedbackClass | null | undefined): FeedbackCommentEntry[] {
    const all = this.parseTicketComments(ticket);
    return all.filter(
      c =>
        !this.isAutomatedResolutionComment(c) &&
        !String(c?.text || '')
          .trim()
          .startsWith('🆕'),
    );
  }

  /**
   * Determina se o ticket foi reaberto (está ativo após já ter sido resolvido/fechado ou tem evento de reabertura ativo)
   */
  isTicketReopened(ticket: AdminFeedbackClass | null | undefined): boolean {
    if (!ticket) return false;
    const status = ticket.Status || 'Pendente';
    const isCurrentlyOpen = this.OPEN_STATUSES.includes(status);
    const allComments = this.parseTicketComments(ticket);

    // Verificar se existe comentário explícito de reabertura
    let lastReopenIdx = -1;
    let lastResolutionIdx = -1;
    allComments.forEach((c, idx) => {
      if (this.isReopenComment(c)) lastReopenIdx = idx;
      if (this.isAutomatedResolutionComment(c)) lastResolutionIdx = idx;
    });

    if (lastReopenIdx !== -1 && (isCurrentlyOpen || lastReopenIdx > lastResolutionIdx)) {
      return true;
    }

    // Se está atualmente num estado aberto mas já teve guia de resolução 🚀 ou DateResolved/AppliedDate preenchido
    if (isCurrentlyOpen && (lastResolutionIdx !== -1 || !!ticket.DateResolved || !!ticket.AppliedDate)) {
      return true;
    }

    return false;
  }

  /**
   * Verifica se o ticket tem informação/comentários adicionados pelo utilizador ou observações
   */
  hasUserInformation(ticket: AdminFeedbackClass | null | undefined): boolean {
    if (!ticket) return false;
    if (this.getUserComments(ticket).length > 0) return true;
    if (ticket.Observation && ticket.Observation.trim().length > 0) return true;
    return false;
  }

  /**
   * Converte qualquer string de data num timestamp numérico seguro
   */
  parseSafeTimestamp(dateStr?: string | null): number {
    if (!dateStr) return 0;
    const normalized = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
    const ts = new Date(normalized).getTime();
    if (!isNaN(ts)) return ts;
    const fallback = new Date(dateStr).getTime();
    return isNaN(fallback) ? 0 : fallback;
  }

  /**
   * Calcula a informação detalhada sobre a última intervenção no ticket
   */
  getLastInterventionInfo(ticket: AdminFeedbackClass | null | undefined): TicketInterventionInfo {
    if (!ticket) {
      return {
        timestamp: 0,
        type: 'new',
        author: '',
        text: '',
        date: '',
        commentCount: 0,
        userCommentCount: 0,
        isReopened: false,
        reopenCount: 0,
        latestUserComment: null,
        latestReopenComment: null,
      };
    }

    const allComments = this.parseTicketComments(ticket);
    const userComments = allComments.filter(c => !this.isAutomatedResolutionComment(c));
    const reopenComments = allComments.filter(c => this.isReopenComment(c));
    const isReopened = this.isTicketReopened(ticket);

    const startTs = this.parseSafeTimestamp(ticket.DateStart);
    const updateTs = this.parseSafeTimestamp(ticket.DateUpdate);
    const resolvedTs = this.parseSafeTimestamp(ticket.DateResolved);

    let maxCommentTs = 0;
    for (const c of allComments) {
      const cTs = this.parseSafeTimestamp(c.date);
      if (cTs > maxCommentTs) maxCommentTs = cTs;
    }

    const latestUserComment = userComments.length > 0 ? userComments[userComments.length - 1] : null;
    const latestReopenComment = reopenComments.length > 0 ? reopenComments[reopenComments.length - 1] : null;

    const maxTs = Math.max(startTs, updateTs, resolvedTs, maxCommentTs);

    if (isReopened && latestReopenComment) {
      return {
        timestamp: maxTs || this.parseSafeTimestamp(latestReopenComment.date),
        type: 'reopen',
        author: latestReopenComment.author || ticket.ReportedByName || 'Utilizador',
        text: latestReopenComment.text,
        date: latestReopenComment.date || ticket.DateUpdate || ticket.DateStart || '',
        commentCount: allComments.length,
        userCommentCount: userComments.length,
        isReopened: true,
        reopenCount: Math.max(1, reopenComments.length),
        latestUserComment,
        latestReopenComment,
      };
    }

    if (isReopened) {
      const fallbackComment =
        latestUserComment || (allComments.length > 0 ? allComments[allComments.length - 1] : null);
      return {
        timestamp: maxTs,
        type: 'reopen',
        author: fallbackComment?.author || ticket.ReportedByName || 'Utilizador',
        text:
          fallbackComment && !this.isAutomatedResolutionComment(fallbackComment)
            ? fallbackComment.text
            : `🔄 Ticket reaberto (Estado atual: ${ticket.Status || 'Pendente'})`,
        date: fallbackComment?.date || ticket.DateUpdate || ticket.DateStart || '',
        commentCount: allComments.length,
        userCommentCount: userComments.length,
        isReopened: true,
        reopenCount: Math.max(1, reopenComments.length),
        latestUserComment,
        latestReopenComment,
      };
    }

    if (latestUserComment) {
      const entryType = this.isAssignmentComment(latestUserComment)
        ? 'assignment'
        : this.isStatusComment(latestUserComment)
          ? 'status'
          : 'comment';
      return {
        timestamp: maxTs || this.parseSafeTimestamp(latestUserComment.date),
        type: entryType,
        author: latestUserComment.author || 'Utilizador',
        text: latestUserComment.text,
        date: latestUserComment.date || ticket.DateUpdate || ticket.DateStart || '',
        commentCount: allComments.length,
        userCommentCount: userComments.length,
        isReopened: false,
        reopenCount: reopenComments.length,
        latestUserComment,
        latestReopenComment,
      };
    }

    if (ticket.Observation && ticket.Observation.trim()) {
      return {
        timestamp: maxTs,
        type: 'comment',
        author: ticket.AppliedBy || ticket.ReportedByName || 'Equipa',
        text: ticket.Observation.trim(),
        date: ticket.DateUpdate || ticket.DateStart || '',
        commentCount: allComments.length,
        userCommentCount: 1,
        isReopened: false,
        reopenCount: 0,
        latestUserComment: null,
        latestReopenComment: null,
      };
    }

    if (updateTs > startTs + 5000) {
      return {
        timestamp: maxTs,
        type: 'status',
        author: ticket.AppliedBy || ticket.ReportedByName || 'Sistema',
        text: `Estado atualizado para ${ticket.Status || 'Pendente'}`,
        date: ticket.DateUpdate || ticket.DateStart || '',
        commentCount: allComments.length,
        userCommentCount: 0,
        isReopened: false,
        reopenCount: 0,
        latestUserComment: null,
        latestReopenComment: null,
      };
    }

    return {
      timestamp: maxTs,
      type: 'new',
      author: ticket.ReportedByName || ticket.CreatedByName || 'Utilizador',
      text: ticket.Description || ticket.Title || '',
      date: ticket.DateStart || '',
      commentCount: allComments.length,
      userCommentCount: 0,
      isReopened: false,
      reopenCount: 0,
      latestUserComment: null,
      latestReopenComment: null,
    };
  }

  private getSeenMap(): Record<number, number> {
    if (this.seenCache) return this.seenCache;
    try {
      const raw = localStorage.getItem(this.SEEN_STORAGE_KEY);
      this.seenCache = raw ? JSON.parse(raw) : {};
    } catch {
      this.seenCache = {};
    }
    return this.seenCache!;
  }

  private persistSeenMap(): void {
    if (!this.seenCache) return;
    try {
      localStorage.setItem(this.SEEN_STORAGE_KEY, JSON.stringify(this.seenCache));
    } catch {
      // ignore storage errors
    }
  }

  /**
   * Inicializa o baseline na primeira utilização para não marcar centenas de tickets antigos já fechados como "Por abrir",
   * mantendo como "Por abrir" os tickets abertos/pendentes, reabertos ou com comentários recentes.
   */
  initializeBaselineSeenIfNeeded(tickets: AdminFeedbackClass[]): void {
    if (!Array.isArray(tickets) || tickets.length === 0) return;
    try {
      const alreadyInitialized = localStorage.getItem(this.SEEN_BASELINE_KEY);
      if (alreadyInitialized) return;

      const map = this.getSeenMap();
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

      for (const t of tickets) {
        const id = t.idAdminFeedback;
        if (!id) continue;
        if (map[id] !== undefined) continue;

        const info = this.getLastInterventionInfo(t);
        const isClosed = this.CLOSED_STATUSES.includes(t.Status || '');
        const hasRecentUserActivity = info.isReopened || (info.userCommentCount > 0 && info.timestamp >= sevenDaysAgo);

        // Se o ticket já está fechado e não foi reaberto nem tem comentários recentes, marca como já visto no baseline
        if (isClosed && !hasRecentUserActivity) {
          map[id] = Math.max(info.timestamp, Date.now());
        } else if (!info.isReopened && info.userCommentCount === 0 && info.timestamp < sevenDaysAgo) {
          // Tickets antigos sem comentários novos nem reabertura também começam vistos para destacar os realmente novos/intervencionados
          map[id] = Math.max(info.timestamp, Date.now());
        }
      }

      this.persistSeenMap();
      localStorage.setItem(this.SEEN_BASELINE_KEY, '1');
    } catch {
      // ignore
    }
  }

  /**
   * Verifica se um ticket tem alguma intervenção que o utilizador ainda não abriu/viu
   */
  isTicketUnread(ticket: AdminFeedbackClass | null | undefined): boolean {
    if (!ticket || !ticket.idAdminFeedback) return false;
    const map = this.getSeenMap();
    const lastSeen = map[ticket.idAdminFeedback] || 0;
    if (lastSeen === -1) return true; // marcado manualmente como não visto
    const info = this.getLastInterventionInfo(ticket);
    // Margem de 1000ms para evitar falsos positivos no próprio segundo em que o utilizador interage
    return info.timestamp > lastSeen + 1000;
  }

  /**
   * Marca um ticket como visto/aberto após a última intervenção
   */
  markTicketAsSeen(ticketOrId: AdminFeedbackClass | number | null | undefined): void {
    if (!ticketOrId) return;
    const id = typeof ticketOrId === 'number' ? ticketOrId : ticketOrId.idAdminFeedback;
    if (!id) return;

    const map = this.getSeenMap();
    let targetTs = Date.now();
    if (typeof ticketOrId === 'object') {
      const info = this.getLastInterventionInfo(ticketOrId);
      targetTs = Math.max(targetTs, info.timestamp);
    }
    map[id] = targetTs;
    this.persistSeenMap();
  }

  /**
   * Marca manualmente um ticket como "Por Abrir / Não Visto"
   */
  markTicketAsUnread(ticketOrId: AdminFeedbackClass | number | null | undefined): void {
    if (!ticketOrId) return;
    const id = typeof ticketOrId === 'number' ? ticketOrId : ticketOrId.idAdminFeedback;
    if (!id) return;

    const map = this.getSeenMap();
    map[id] = -1;
    this.persistSeenMap();
  }

  /**
   * Alterna entre visto e não visto
   */
  toggleTicketUnread(ticket: AdminFeedbackClass): boolean {
    if (this.isTicketUnread(ticket)) {
      this.markTicketAsSeen(ticket);
      return false;
    } else {
      this.markTicketAsUnread(ticket);
      return true;
    }
  }

  /**
   * Marca uma lista de tickets como vistos
   */
  markAllTicketsAsSeen(tickets: AdminFeedbackClass[]): void {
    if (!Array.isArray(tickets)) return;
    const map = this.getSeenMap();
    const now = Date.now();
    for (const t of tickets) {
      if (!t.idAdminFeedback) continue;
      const info = this.getLastInterventionInfo(t);
      map[t.idAdminFeedback] = Math.max(now, info.timestamp);
    }
    this.persistSeenMap();
  }

  // ============================================================================
  // IDENTIDADE ATIVA DO UTILIZADOR & NOTIFICAÇÕES DISCRETAS EM TEMPO REAL
  // ============================================================================

  /**
   * Regista que o diálogo ou ecrã de gestão de feedback está aberto no ecrã
   */
  registerViewer(): void {
    this.activeViewerCount++;
  }

  unregisterViewer(): void {
    this.activeViewerCount = Math.max(0, this.activeViewerCount - 1);
  }

  /**
   * Normaliza um nome de pessoa para comparação resiliente (sem acentos, minúsculas)
   */
  normalizePersonName(name?: string | null): string {
    return String(name || '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  /**
   * Guarda a identidade ativa do utilizador (ex.: quando seleciona "Eu sou:" ou interage num ticket)
   */
  setActiveUserIdentity(name: string | null | undefined): void {
    const clean = String(name || '').trim();
    if (
      !clean ||
      clean.toLowerCase() === 'administrador' ||
      clean.toLowerCase() === 'admin' ||
      clean.toLowerCase() === 'utilizador'
    ) {
      return;
    }
    try {
      localStorage.setItem(this.ACTIVE_IDENTITY_STORAGE_KEY, clean);
    } catch {
      // ignore
    }
  }

  /**
   * Obtém a identidade principal ativa do utilizador atual (ex.: 'João Vilar', 'Hugo Capela', etc.)
   */
  getPrimaryUserIdentity(): string {
    const isGeneric = (val: string) => {
      const low = val.toLowerCase();
      return !low || low === 'administrador' || low === 'admin' || low === 'utilizador';
    };

    try {
      const stored = String(localStorage.getItem(this.ACTIVE_IDENTITY_STORAGE_KEY) || '').trim();
      if (!isGeneric(stored)) {
        return stored;
      }
    } catch {
      // ignore
    }

    const u: any = this.globalService?.loggedUserClass;
    const candidates = [u?.Name, u?.commercialClass?.Name, u?.customerClass?.Name, u?.CommercialName];
    for (const c of candidates) {
      const clean = String(c || '').trim();
      if (!isGeneric(clean)) {
        return clean;
      }
    }

    return DEFAULT_FEEDBACK_ASSIGNEE;
  }

  /**
   * Obtém a lista de nomes que identificam o utilizador atual na sessão ativa
   */
  getActiveUserIdentities(): string[] {
    const primary = this.getPrimaryUserIdentity();
    return primary ? [primary] : [DEFAULT_FEEDBACK_ASSIGNEE];
  }

  /**
   * Verifica se um determinado nome corresponde ao utilizador ativo neste navegador
   */
  isNameMatchingCurrentUser(targetName?: string | null): boolean {
    const normTarget = this.normalizePersonName(targetName);
    if (!normTarget) return false;
    const identities = this.getActiveUserIdentities();
    return identities.some(id => this.normalizePersonName(id) === normTarget);
  }

  /**
   * Verifica se o ticket está atribuído / dirigido ao utilizador atual ("Para Mim")
   */
  isTicketAssignedToMe(ticket: AdminFeedbackClass | null | undefined): boolean {
    if (!ticket) return false;
    const assignee = this.resolveTicketAssignee(ticket);
    return this.isNameMatchingCurrentUser(assignee);
  }

  /**
   * Verifica se o ticket foi reportado / criado pelo utilizador atual ("Meu Pedido")
   */
  isTicketReportedByMe(ticket: AdminFeedbackClass | null | undefined): boolean {
    if (!ticket) return false;
    const reporter = String(
      ticket.ReportedByName || (ticket as any).CreatedByName || (ticket as any).UserCreateName || '',
    ).trim();
    return this.isNameMatchingCurrentUser(reporter);
  }

  /**
   * Verifica se o ticket pertence ao utilizador atual (atribuído a mim OU criado por mim)
   */
  isMyTicket(ticket: AdminFeedbackClass | null | undefined): boolean {
    return this.isTicketAssignedToMe(ticket) || this.isTicketReportedByMe(ticket);
  }

  /**
   * Garante que a ligação Socket.IO e a escuta de notificações de tickets estão ativas
   */
  ensureRealtimeListening(): void {
    this.initRealtimeNotifications();
    if (this.globalService?.loggedUserClass?.Token) {
      this.realtimeService.connect();
    }
  }

  private isDuplicateNotification(key: string): boolean {
    const now = Date.now();
    const prev = this.recentNotificationKeys.get(key);
    if (prev && now - prev < 5000) {
      return true;
    }
    this.recentNotificationKeys.set(key, now);
    if (this.recentNotificationKeys.size > 50) {
      for (const [k, ts] of this.recentNotificationKeys.entries()) {
        if (now - ts > 15000) this.recentNotificationKeys.delete(k);
      }
    }
    return false;
  }

  private initRealtimeNotifications(): void {
    if (this.isRealtimeNotificationsHooked || typeof window === 'undefined') return;
    this.isRealtimeNotificationsHooked = true;

    // 1. Novo ticket criado e dirigido ao utilizador atual
    this.realtimeService.feedbackCreated$.subscribe((newItem: any) => {
      if (!newItem || !newItem.idAdminFeedback) return;
      const ticketId = Number(newItem.idAdminFeedback);
      const assignee = this.resolveTicketAssignee(newItem);
      const reporter = String(newItem.ReportedByName || newItem.CreatedByName || '').trim();

      // Só notificar se for dirigido ao utilizador atual e tiver sido criado por outra pessoa
      if (this.isNameMatchingCurrentUser(assignee) && (!reporter || !this.isNameMatchingCurrentUser(reporter))) {
        const dedupKey = `created_${ticketId}_${assignee}`;
        if (this.isDuplicateNotification(dedupKey)) return;
        this.markTicketAsUnread(ticketId);
        this.showDiscreetTicketNotification({
          ticketId,
          icon: '📩',
          badgeLabel: 'Novo ticket dirigido a si',
          title: `#${ticketId} · ${newItem.Title || 'Sem título'}`,
          subtitle: reporter ? `Enviado por ${reporter}` : 'Novo pedido de feedback',
          accentColor: '#2563eb',
        });
      }
    });

    // 2. Ticket passado/atribuído ao utilizador atual ou nova questão/comentário num ticket seu
    this.realtimeService.feedbackUpdated$.subscribe((payload: { id: number; patch: any } | null) => {
      if (!payload || !payload.id || !payload.patch) return;
      const ticketId = Number(payload.id);
      const patch = payload.patch;

      // Caso A: Passagem / Atribuição de ticket
      if (patch.assignmentChanged && patch.assignmentChanged.to) {
        const toUser = String(patch.assignmentChanged.to).trim();
        const byUser = String(patch.assignmentChanged.by || '').trim();
        const note = String(patch.assignmentChanged.note || '').trim();
        const ticketTitle = String(patch.assignmentChanged.title || patch.Title || '').trim();

        if (this.isNameMatchingCurrentUser(toUser) && (!byUser || !this.isNameMatchingCurrentUser(byUser))) {
          const dedupKey = `assign_${ticketId}_${toUser}_${note}`;
          if (this.isDuplicateNotification(dedupKey)) return;
          this.markTicketAsUnread(ticketId);
          this.showDiscreetTicketNotification({
            ticketId,
            icon: '↪️',
            badgeLabel: `Ticket #${ticketId} passado para si`,
            title: ticketTitle ? `#${ticketId} · ${ticketTitle}` : `Ticket #${ticketId}`,
            subtitle: note
              ? `${byUser || 'Colega'}: "${note.length > 75 ? note.slice(0, 75) + '…' : note}"`
              : `Atribuído por ${byUser || 'um colega'}`,
            accentColor: '#7c3aed',
          });
          return;
        }
      }

      // Caso B: Novo comentário / questão num ticket atribuído a si ou reportado por si
      if (patch.lastComment && patch.lastComment.text && patch.lastComment.type !== 'assignment') {
        const comment = patch.lastComment;
        const commentAuthor = String(comment.author || '').trim();
        if (commentAuthor && this.isNameMatchingCurrentUser(commentAuthor)) {
          return; // Ignorar comentários feitos pelo próprio utilizador
        }
        if (this.isAutomatedResolutionComment(comment) || this.isStatusComment(comment)) {
          return;
        }

        const assignedTo = String(patch.AssignedToName || DEFAULT_FEEDBACK_ASSIGNEE).trim();
        const reportedBy = String(patch.ReportedByName || '').trim();
        const isRelevantToMe =
          this.isNameMatchingCurrentUser(assignedTo) ||
          (reportedBy ? this.isNameMatchingCurrentUser(reportedBy) : false);

        if (isRelevantToMe) {
          const cleanText = String(comment.text || '').trim();
          const dedupKey = `comment_${ticketId}_${comment.id || cleanText.slice(0, 30)}`;
          if (this.isDuplicateNotification(dedupKey)) return;
          this.markTicketAsUnread(ticketId);
          const ticketTitle = String(patch.ticketTitle || patch.Title || '').trim();
          this.showDiscreetTicketNotification({
            ticketId,
            icon: '💬',
            badgeLabel: `Nova questão/resposta no Ticket #${ticketId}`,
            title: ticketTitle ? `#${ticketId} · ${ticketTitle}` : `Ticket #${ticketId}`,
            subtitle: `${commentAuthor || 'Utilizador'}: "${cleanText.length > 80 ? cleanText.slice(0, 80) + '…' : cleanText}"`,
            accentColor: '#0284c7',
          });
        }
      }
    });
  }

  /**
   * Abre um ticket específico: se já existir visualizador aberto emite evento, caso contrário abre o diálogo no ticket
   */
  openTicketFromNotification(ticketId: number): void {
    if (!ticketId) return;
    if (this.activeViewerCount > 0) {
      this.openTicketRequest$.next(ticketId);
      return;
    }
    try {
      const dialog = this.injector.get(MatDialog, null);
      if (!dialog) return;
      import('../components/admin-feedback/admin-feedback-dialog/admin-feedback-dialog.component').then(m => {
        const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
        dialog.open(m.AdminFeedbackDialogComponent, {
          width: isMobile ? '100vw' : '96vw',
          maxWidth: isMobile ? '100vw' : '96vw',
          height: isMobile ? '100%' : '92vh',
          maxHeight: isMobile ? '100%' : '94vh',
          position: isMobile ? { top: '0px', left: '0px' } : undefined,
          autoFocus: false,
          panelClass: 'admin-feedback-dialog-panel',
          data: {
            initialTab: 1,
            expandTicketId: ticketId,
          },
        });
      });
    } catch (err) {
      console.warn('[AdminFeedback] Não foi possível abrir o diálogo a partir da notificação:', err);
    }
  }

  /**
   * Apresenta uma notificação flutuante ultra discreta e clean no canto inferior direito do ecrã
   */
  showDiscreetTicketNotification(opts: {
    ticketId: number;
    icon: string;
    badgeLabel: string;
    title: string;
    subtitle: string;
    accentColor?: string;
  }): void {
    if (typeof document === 'undefined' || !document.body) return;

    const accent = opts.accentColor || '#1b1464';

    // Remover notificação anterior do mesmo ticket se existir
    const existing = document.getElementById(`sv-feedback-live-pill-${opts.ticketId}`);
    if (existing) existing.remove();

    let container = document.getElementById('sv-feedback-live-notifications-host');
    if (!container) {
      container = document.createElement('div');
      container.id = 'sv-feedback-live-notifications-host';
      Object.assign(container.style, {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: '99998',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '350px',
        width: 'calc(100vw - 32px)',
        pointerEvents: 'none',
      });
      document.body.appendChild(container);
    }

    const card = document.createElement('div');
    card.id = `sv-feedback-live-pill-${opts.ticketId}`;
    Object.assign(card.style, {
      pointerEvents: 'auto',
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(15, 23, 42, 0.09)',
      borderLeft: `3.5px solid ${accent}`,
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06)',
      padding: '10px 12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      opacity: '0',
      transform: 'translateY(10px)',
      transition: 'opacity 0.22s ease, transform 0.22s ease',
    });

    const topRow = document.createElement('div');
    Object.assign(topRow.style, {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    });

    const badgeWrap = document.createElement('div');
    Object.assign(badgeWrap.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '11px',
      fontWeight: '700',
      color: accent,
      letterSpacing: '0.01em',
    });
    const iconSpan = document.createElement('span');
    iconSpan.textContent = opts.icon;
    iconSpan.style.fontSize = '13px';
    const badgeText = document.createElement('span');
    badgeText.textContent = opts.badgeLabel;
    badgeWrap.appendChild(iconSpan);
    badgeWrap.appendChild(badgeText);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = '✕';
    closeBtn.title = 'Fechar notificação';
    Object.assign(closeBtn.style, {
      border: 'none',
      background: 'transparent',
      color: '#94a3b8',
      fontSize: '12px',
      lineHeight: '1',
      padding: '2px 4px',
      cursor: 'pointer',
      borderRadius: '4px',
    });

    topRow.appendChild(badgeWrap);
    topRow.appendChild(closeBtn);

    const titleEl = document.createElement('div');
    titleEl.textContent = opts.title;
    Object.assign(titleEl.style, {
      fontSize: '12px',
      fontWeight: '600',
      color: '#1e293b',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });

    const subEl = document.createElement('div');
    subEl.textContent = opts.subtitle;
    Object.assign(subEl.style, {
      fontSize: '11px',
      color: '#64748b',
      lineHeight: '1.35',
      display: '-webkit-box',
      webkitLineClamp: '2',
      webkitBoxOrient: 'vertical',
      overflow: 'hidden',
    });

    const footerRow = document.createElement('div');
    Object.assign(footerRow.style, {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '6px',
      marginTop: '2px',
    });

    const openBtn = document.createElement('button');
    openBtn.type = 'button';
    openBtn.textContent = 'Ver ticket →';
    Object.assign(openBtn.style, {
      border: 'none',
      background: '#1b1464',
      color: '#ffffff',
      fontSize: '11px',
      fontWeight: '600',
      padding: '4px 10px',
      borderRadius: '6px',
      cursor: 'pointer',
    });

    footerRow.appendChild(openBtn);

    card.appendChild(topRow);
    card.appendChild(titleEl);
    card.appendChild(subEl);
    card.appendChild(footerRow);
    container.appendChild(card);

    requestAnimationFrame(() => {
      card.style.opacity = '1';
      card.style.transform = 'translateY(0)';
    });

    let dismissTimeout: any = null;
    const removeCard = () => {
      if (dismissTimeout) clearTimeout(dismissTimeout);
      card.style.opacity = '0';
      card.style.transform = 'translateY(8px)';
      setTimeout(() => {
        card.remove();
      }, 220);
    };

    const startTimer = () => {
      if (dismissTimeout) clearTimeout(dismissTimeout);
      dismissTimeout = setTimeout(removeCard, 12000);
    };

    card.addEventListener('mouseenter', () => {
      if (dismissTimeout) clearTimeout(dismissTimeout);
    });
    card.addEventListener('mouseleave', () => {
      startTimer();
    });

    closeBtn.addEventListener('click', e => {
      e.stopPropagation();
      removeCard();
    });

    openBtn.addEventListener('click', e => {
      e.stopPropagation();
      removeCard();
      this.openTicketFromNotification(opts.ticketId);
    });

    startTimer();
  }

  /**
   * Obter preferências de notificação da equipa
   */
  getTeamPreferences(): Observable<{ success: boolean; data: AdminFeedbackTeamPreference[] }> {
    return this.http.get<{ success: boolean; data: AdminFeedbackTeamPreference[] }>(`${this.baseUrl}/team/preferences`);
  }

  /**
   * Gravar/actualizar preferência de notificação de um membro da equipa
   */
  saveTeamPreferences(pref: {
    Name: string;
    Email?: string;
    NotifyByEmail: boolean;
  }): Observable<{ success: boolean; data?: any; message?: string }> {
    return this.http.post<{ success: boolean; data?: any; message?: string }>(`${this.baseUrl}/team/preferences`, pref);
  }
}
